/*
 * R24 storefront tracker — page views to the R24 Gateway.
 *
 * This repo is the source of truth. A Gateway re-generation must emit exactly
 * this contract (docs/tracking/PIXEL-SERVER.md), guarded by
 * scripts/tracking-pixel.contract.test.mjs:
 *
 *   1. No secret in the browser. Everything in this file and in the <script>
 *      tag's data-* attributes is public; the server validates instead.
 *   2. CORS-simple delivery: the body is JSON text sent as text/plain, with no
 *      custom headers, so the browser sends no preflight. The server parses the
 *      body as JSON regardless of the Content-Type.
 *   3. Consent first (GDPR/TTDSG, shop region DE): nothing is sent and nothing
 *      is written to localStorage/sessionStorage until Shopify's Customer
 *      Privacy API reports analytics consent. Events are queued until then and
 *      dropped if consent is denied. No API = no consent = nothing is sent.
 *   4. Data minimisation: `path` keeps only allow-listed query parameters
 *      (no click ids, no e-mail-like params), `ref` is the referrer ORIGIN only
 *      (an order-status or checkout URL carries a token in its path/query).
 *      The visitor id is created after consent under `r24_vid`; the old
 *      pre-consent `r24_cid` (written without consent before 2026-09-21) and
 *      `r24_last_click` are deleted, never reused, and a later "deny" deletes
 *      the ids again.
 *
 * The placement/notification runtime that used to live here (≈90 % of the
 * file) was removed on 2026-09-21: no Liquid renders a placement wrapper
 * (snippets/r24-placement-* are unused) and notifications were disabled in
 * r24-bootstrap since 2026-07-15. Evidence: docs/tracking/PIXEL-SERVER.md.
 */
(function () {
  'use strict';

  // Own events only from people: automation (navigator.webdriver, empty
  // navigator.languages, HeadlessChrome UA) sends nothing. Shared with the
  // planner's kpEvt (snippets/r24-kuechenplaner-mobil.liquid) - whoever runs
  // first defines it. Nothing is hidden or blocked; only our own sending stops.
  window.r24IsAutomated = window.r24IsAutomated || function () {
    try {
      return navigator.webdriver === true || !(navigator.languages && navigator.languages.length) || /HeadlessChrome/.test(navigator.userAgent);
    } catch (e) {
      return false;
    }
  };

  var s = document.currentScript;
  if (!s) return;
  var endpoint = s.getAttribute('data-r24-endpoint');
  var accountId = s.getAttribute('data-r24-account');
  if (!endpoint || !accountId) return;
  var template = s.getAttribute('data-r24-template') || '';
  var productId = s.getAttribute('data-r24-product-id') || '';
  var collectionId = s.getAttribute('data-r24-collection-id') || '';
  var currency = s.getAttribute('data-r24-currency') || '';
  var country = s.getAttribute('data-r24-country') || '';

  /* ---------------------------------------------------------------- ids -- */
  function uid() {
    var c = window.crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    return 'r24-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
  // Both helpers touch device storage — they run only after consent (deliver).
  // Storage can be blocked (even reading window.localStorage throws then):
  // the id lives for this page only, which is the correct degradation.
  function stored(area, key, prefix) {
    try {
      var store = window[area];
      var v = store.getItem(key);
      if (!v) { v = uid(); store.setItem(key, v); }
      return v;
    } catch (e) {
      return prefix + uid();
    }
  }
  function client() { return stored('localStorage', 'r24_vid', 'anon-'); }
  function sess() { return stored('sessionStorage', 'r24_sid', 'sess-'); }
  // Deleting is always allowed (no consent needed). Blocked storage throws on
  // access — then there is nothing stored to delete, so the throw is expected.
  function forget(area, keys) {
    try { for (var i = 0; i < keys.length; i++) window[area].removeItem(keys[i]); } catch (e) { /* storage blocked: nothing stored */ }
  }
  forget('localStorage', ['r24_cid', 'r24_last_click']);

  var KEEP = ['variant', 'q', 'page', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  // Kundenkonto-Pfade tragen Tokens im PFAD (/account/reset/<id>/<token>,
  // /account/activate/…, /account/orders/<token>) → nur /account/<bereich>.
  function cleanPathname() {
    var m = /^\/(account|customers)(\/[^\/]+)?/.exec(location.pathname || '');
    return m ? m[0] : location.pathname;
  }
  function cleanPath() {
    var out = [], p = String(location.search || '').replace(/^\?/, '').split('&');
    for (var i = 0; i < p.length; i++) {
      var k = '';
      // Kaputte Kodierung (z. B. „?x%=1“) wirft URIError — diesen Parameter
      // auslassen, der Seitenaufruf selbst wird trotzdem gezaehlt.
      try { k = decodeURIComponent((p[i].split('=')[0] || '').replace(/\+/g, ' ')); } catch (e) { continue; }
      if (k && KEEP.indexOf(k) > -1) out.push(p[i]);
    }
    return cleanPathname() + (out.length ? '?' + out.join('&') : '');
  }
  function cleanRef() {
    var m = /^(https?:\/\/[^\/?#]+)/i.exec(document.referrer || '');
    return m ? m[1].toLowerCase() : null;
  }

  /* ----------------------------------------------------------- delivery -- */
  function deliver(rec) {
    if (window.r24IsAutomated()) return; // bots/headless: no sending, no ids written
    var event = {
      id: rec.id,
      ts: rec.ts,
      type: rec.type,
      clientId: client(),
      sessionId: sess(),
      path: rec.path,
      title: rec.title,
      productId: productId || null,
      collectionId: collectionId || null,
      currency: currency || null,
      country: country || null,
      ref: rec.ref,
      cart: rec.cart || null,
      att: sessionAtt(rec.land),
      ctx: rec.ctx || null
    };
    var body = JSON.stringify({ accountId: accountId, event: event });

    // text/plain is a CORS-safelisted Content-Type: no preflight, and the
    // beacon goes out in no-cors mode, so a missing Access-Control-Allow-Origin
    // on the server can no longer produce console errors.
    if (navigator.sendBeacon) {
      var queued = false;
      try {
        queued = navigator.sendBeacon(endpoint, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
      } catch (e) {
        queued = false; // e.g. beacon quota exceeded — fall through to fetch
      }
      if (queued) return;
    }
    if (typeof fetch !== 'function') return;
    // A string body is sent as text/plain;charset=UTF-8. No custom headers,
    // no-cors: the response is opaque and never needed.
    fetch(endpoint, { method: 'POST', body: body, mode: 'no-cors', keepalive: true })
      .catch(function () { /* analytics loss is acceptable; never surface it to the shopper */ });
  }

  /* ------------------------------------------------------------ consent -- */
  var MAX_QUEUE = 20;
  var consent = 'pending'; // 'pending' | 'granted' | 'denied'
  var queue = [];

  function grant() {
    if (consent === 'granted') return;
    consent = 'granted';
    var q = queue;
    queue = [];
    for (var i = 0; i < q.length; i++) deliver(q[i]);
  }
  function deny() {
    consent = 'denied';
    queue = [];
    forget('localStorage', ['r24_vid']);
    forget('sessionStorage', ['r24_sid']);
  }
  function apiAllows() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') return false;
    try {
      return cp.analyticsProcessingAllowed() === true;
    } catch (e) {
      return false; // fail closed: an API that cannot answer has not consented
    }
  }

  // Fired by Shopify's cookie banner whenever the visitor decides (or changes
  // the decision). A later "deny" stops further sending.
  document.addEventListener('visitorConsentCollected', function (e) {
    var d = e && e.detail;
    var allowed = d && typeof d.analyticsAllowed === 'boolean' ? d.analyticsAllowed : apiAllows();
    if (allowed) grant();
    else deny();
  });

  if (apiAllows()) {
    grant();
  } else {
    var S = window.Shopify;
    if (S && typeof S.loadFeatures === 'function' && !S.customerPrivacy) {
      try {
        S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
          if (!err && apiAllows()) grant();
        });
      } catch (e) {
        /* fail closed: without the API nothing is sent */
      }
    }
    // No Shopify.loadFeatures at all → stay 'pending': nothing is sent unless a
    // visitorConsentCollected event explicitly allows analytics.
  }

  /* ---------------------------------------------------- Herkunft (24.10.) --
   * Kampagnen-Herkunft der Sitzung fuer die eigene Zuordnung (Portal: openai-ads/attribution): utm_* und OB
   * ein oppref ankam (o:1 — nie der Wert). Bis zur Einwilligung nur im Speicher dieser Seite (rec.land),
   * erst beim Senden in sessionStorage — wie alle anderen Kennungen dieses Trackers. */
  function landTouch() {
    try {
      var u = new URLSearchParams(location.search), s = u.get('utm_source') || '', o = u.get('oppref') ? 1 : 0;
      if (!s && !o) return null;
      var c = function (v) { return String(v || '').slice(0, 80); };
      return { s: c(s).toLowerCase(), m: c(u.get('utm_medium')).toLowerCase(), c: c(u.get('utm_campaign')), a: c(u.get('utm_content')), g: c(u.get('utm_term')), o: o, p: cleanPathname().slice(0, 120) };
    } catch (e) { return null; }
  }
  function sessionAtt(land) {
    try {
      if (land) { sessionStorage.setItem('r24_att', JSON.stringify(land)); return land; }
      return JSON.parse(sessionStorage.getItem('r24_att') || 'null');
    } catch (e) { return land || null; }
  }

  /* -------------------------------------------------------------- track -- */
  function track(type, cart, ctx) {
    if (consent === 'denied') return;
    var rec = {
      id: uid(),
      ts: new Date().toISOString(),
      type: type,
      path: cleanPath(),
      title: document.title,
      ref: cleanRef(),
      cart: cart || null,
      land: landTouch(),
      ctx: ctx || null
    };
    if (consent === 'granted') deliver(rec);
    else if (queue.length < MAX_QUEUE) queue.push(rec);
  }

  /* ------------------------------------------------------ Warenkorb-Stand --
   * Fuer den Warenkorb-Monitor (Portal): gleiche Regeln wie jedes Ereignis,
   * ohne Personendaten. Der Cart-Token verlaesst den Browser nie, nur ein
   * SHA-256-Kurzhash (der Token oeffnet den Warenkorb). */
  var lastCart = null;
  function hashKey(token) {
    var c = window.crypto;
    if (!token || !c || !c.subtle || typeof TextEncoder !== 'function') return Promise.resolve(null);
    return c.subtle.digest('SHA-256', new TextEncoder().encode('r24|' + token)).then(function (buf) {
      var b = new Uint8Array(buf), h = '';
      for (var i = 0; i < 16; i++) h += (b[i] < 16 ? '0' : '') + b[i].toString(16);
      return h;
    }).catch(function () { return null; });
  }
  function snapshot(cartJson) {
    if (!cartJson || !cartJson.token) return Promise.resolve(null);
    return hashKey(cartJson.token).then(function (key) {
      if (!key) return null;
      var items = [], list = cartJson.items || [];
      for (var i = 0; i < list.length && i < 20; i++) {
        var it = list[i];
        items.push({ v: String(it.variant_id), p: it.product_id ? String(it.product_id) : null, q: it.quantity, pr: it.final_line_price, t: String(it.product_title || '').slice(0, 80) });
      }
      return { key: key, n: cartJson.item_count, total: cartJson.total_price, items: items };
    });
  }
  function readCart() {
    if (consent === 'denied' || window.r24IsAutomated() || typeof fetch !== 'function') return Promise.resolve(null);
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(snapshot)
      .catch(function () { return null; });
  }
  function report(type) {
    return readCart().then(function (cart) {
      if (!cart) { if (type === 'cart_view') track(type); return; }
      var prev = lastCart;
      lastCart = cart;
      if (type === 'cart_view') { track(type, cart); return; }
      // Aenderung ohne Unterschied (z. B. Notiz/Attribute) nicht melden.
      if (prev && prev.key === cart.key && prev.total === cart.total && prev.n === cart.n) return;
      track(prev && cart.total < prev.total ? 'cart_remove' : 'cart_add', cart);
    });
  }

  // Alle Aenderungen laufen ueber die Ajax-API: ein Beobachter statt 20 Aufrufstellen.
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var cartT;
  function cartChanged() { clearTimeout(cartT); cartT = setTimeout(function () { report('cart_add'); }, 700); }
  if (typeof window.fetch === 'function') {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var p = origFetch.apply(this, arguments);
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var method = (init && init.method) || (input && input.method) || 'GET';
        if (/^post$/i.test(method) && CART_WRITE.test(url)) {
          p.then(function (r) { if (r && r.ok) cartChanged(); }, function () {});
        }
      } catch (e) { /* Beobachten darf den Abruf nie stoeren */ }
      return p;
    };
  }
  ['cart:updated', 't4s:cart:updated', 'on:cart:add'].forEach(function (ev) {
    document.addEventListener(ev, cartChanged);
  });

  // „Zur Kasse": letzter Stand per Beacon, bevor die Seite wechselt.
  function toCheckout() { if (lastCart) track('checkout_start', lastCart); }
  document.addEventListener('submit', function (e) {
    var sub = e.submitter;
    if (sub && sub.name === 'checkout') toCheckout();
  }, true);
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('a[href^="/checkout"],[name="checkout"]:not([type="submit"])');
    if (a) toCheckout();
  }, true);

  /* ------------------------------------------------ Kuechen-Funnel (24.10.) --
   * Der Kuechenplaner meldet seine Schritte ueber kpEvt (snippets/r24-kuechenplaner-mobil) → hier. Eigene
   * Namen (keine OpenAI-Standardereignisse): planner_start, planner_step_complete, planner_complete,
   * planner_add_to_cart, consultation_request. Je Sitzung zaehlt das Portal jede Stufe einmal. Seiten-Knoepfe
   * koennen data-r24-funnel="<name>" tragen (z. B. Beratung auf der Anzeigen-Landingpage). */
  var FUNNEL = { planner_open: 'planner_start', wizard_open: 'planner_start', wizard_step: 'planner_step_complete',
    wizard_done: 'planner_complete', plan_complete: 'planner_complete', cart_click: 'planner_add_to_cart',
    consult_click: 'consultation_request' };
  var FUNNEL_OK = /^(planner_start|planner_step_complete|planner_complete|planner_add_to_cart|consultation_request)$/;
  window.r24Funnel = function (name, data) {
    var t = FUNNEL[name] || name;
    if (!FUNNEL_OK.test(t)) return;
    var d = data || {}, c = function (v) { return v == null ? '' : String(v).slice(0, 60); };
    track(t, null, { series: c(d.series || d.serie), mode: c(d.mode || 'planer'), step: c(d.step || d.var || name) });
  };
  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest && e.target.closest('[data-r24-funnel]');
    if (el) window.r24Funnel(el.getAttribute('data-r24-funnel'), { mode: el.getAttribute('data-r24-funnel-mode') || 'seite', step: el.getAttribute('data-r24-funnel-step') || '' });
  }, true);

  var type = 'page_view';
  if (template === 'product') type = 'product_view';
  else if (template === 'collection') type = 'collection_view';
  // Warenkorbseite: EIN cart_view mit Stand (ohne Stand als einfacher Aufruf).
  if (template === 'cart') report('cart_view');
  else track(type);
})();
